import type React from "react";
import { SimpleGrid, type StyleProp, Stack, Text } from "@mantine/core";

interface MetadataGridProps {
  metadata: Record<string, unknown>;
  /** Keys to skip (already surfaced elsewhere on the card). */
  omit?: string[];
  cols?: StyleProp<number>;
}

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\bid\b/gi, "ID")
    .replace(/^\w/, (c) => c.toUpperCase());
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function formatValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const MetadataGrid: React.FC<MetadataGridProps> = ({
  metadata,
  omit = [],
  cols = { base: 2, sm: 3, lg: 4 },
}) => {
  const skip = new Set(omit);
  const entries = Object.entries(metadata)
    .filter(([key, value]) => !skip.has(key) && !isEmpty(value))
    .sort(([a], [b]) => a.localeCompare(b));

  if (entries.length === 0) return null;

  return (
    <SimpleGrid cols={cols} spacing="md" verticalSpacing="xs">
      {entries.map(([key, value]) => (
        <Stack key={key} gap={0}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
            {humanizeKey(key)}
          </Text>
          <Text size="sm" className="metadata-value">
            {formatValue(value)}
          </Text>
        </Stack>
      ))}
    </SimpleGrid>
  );
};

export default MetadataGrid;
